import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Star, ShoppingBag, TrendingUp, MessageSquare, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import membershipUpsellImg from "@/assets/templates/membership-upsell.jpg";
import loyaltyConversionImg from "@/assets/templates/loyalty-conversion.jpg";
import merchSalesImg from "@/assets/templates/merch-sales.jpg";
import generalCompetitionImg from "@/assets/templates/general-competition.jpg";
import guestExperienceImg from "@/assets/templates/guest-experience.jpg";
import upsellSprintImg from "@/assets/templates/upsell-sprint.jpg";

export interface CampaignTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  image: string;
  icon: React.ReactNode;
  popular?: boolean;
  config: {
    name: string;
    metric: "memberships" | "value";
    topN: number;
    selectedProducts: string[];
    prize1: string;
    prize2: string;
    prize3: string;
    managerMsg: string;
    showNames: boolean;
    showAvatars: boolean;
    showMovement: boolean;
    showCountdown: boolean;
    showHallOfFame: boolean;
    refreshCadence: number;
  };
}

const templateStats: Record<string, { avgRoi: string; venues: number }> = {
  "membership-upsell": { avgRoi: "+$4,200/mo", venues: 186 },
  "loyalty-conversion": { avgRoi: "+$2,800/mo", venues: 142 },
  "merch-sales": { avgRoi: "+$1,950/mo", venues: 98 },
  "general-competition": { avgRoi: "+$1,600/mo", venues: 214 },
  "guest-experience": { avgRoi: "+$1,100/mo", venues: 67 },
  "upsell-sprint": { avgRoi: "+$2,400/mo", venues: 124 },
};

const campaignTemplates: CampaignTemplate[] = [
  {
    id: "membership-upsell",
    name: "Membership Upsell Blitz",
    description: "Drive membership sales with a head-to-head staff leaderboard. Perfect for converting casual visitors into long-term members.",
    category: "Membership",
    image: membershipUpsellImg,
    icon: <TrendingUp className="h-5 w-5" />,
    popular: true,
    config: {
      name: "Membership Upsell Blitz",
      metric: "memberships",
      topN: 10,
      selectedProducts: ["p1", "p2", "p3", "p4"],
      prize1: "$1,000 Gift Card",
      prize2: "$500 Gift Card",
      prize3: "$250 Gift Card",
      managerMsg: "Every guest is a potential member — let's convert! Show them the value and watch those numbers climb. 🚀",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: true,
      showHallOfFame: true,
      refreshCadence: 10,
    },
  },
  {
    id: "loyalty-conversion",
    name: "Loyalty Conversion Drive",
    description: "Track and reward staff who convert the most guests into your loyalty program. Build your repeat-visit base.",
    category: "Loyalty",
    image: loyaltyConversionImg,
    icon: <Star className="h-5 w-5" />,
    popular: true,
    config: {
      name: "Loyalty Conversion Drive",
      metric: "memberships",
      topN: 10,
      selectedProducts: ["p1", "p2"],
      prize1: "Extra Day Off + $500",
      prize2: "$300 Gift Card",
      prize3: "$150 Gift Card",
      managerMsg: "Loyalty members visit 3x more often — every sign-up you get today pays off for months. Let's build our base! 💪",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: true,
      showHallOfFame: true,
      refreshCadence: 15,
    },
  },
  {
    id: "merch-sales",
    name: "Merch Sales Competition",
    description: "Boost retail and merchandise revenue by gamifying product sales across your team. Track by dollar value sold.",
    category: "Product Sales",
    image: merchSalesImg,
    icon: <ShoppingBag className="h-5 w-5" />,
    config: {
      name: "Merch Sales Competition",
      metric: "value",
      topN: 10,
      selectedProducts: ["p7", "p8"],
      prize1: "$750 Bonus",
      prize2: "$400 Bonus",
      prize3: "$200 Bonus",
      managerMsg: "Merch margins are huge — every Sky Socks upsell and party package counts. Suggest it to every guest! 🧦🎉",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: true,
      showHallOfFame: false,
      refreshCadence: 10,
    },
  },
  {
    id: "general-competition",
    name: "General Staff Competition",
    description: "A flexible, all-purpose competition template. Set your own metric, prizes, and duration for any team challenge.",
    category: "Competition",
    image: generalCompetitionImg,
    icon: <Trophy className="h-5 w-5" />,
    config: {
      name: "Staff Competition",
      metric: "memberships",
      topN: 10,
      selectedProducts: ["p1", "p2", "p3", "p4", "p5", "p6"],
      prize1: "Winner's Trophy + $500",
      prize2: "$250 Gift Card",
      prize3: "$100 Gift Card",
      managerMsg: "Let's see who's got what it takes! Give it your all this week — may the best performer win! 🏆",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: true,
      showHallOfFame: true,
      refreshCadence: 10,
    },
  },
  {
    id: "guest-experience",
    name: "Guest Experience Score",
    description: "Rank staff by their customer feedback scores. Celebrate those delivering exceptional guest experiences.",
    category: "Feedback",
    image: guestExperienceImg,
    icon: <MessageSquare className="h-5 w-5" />,
    config: {
      name: "Guest Experience Leaderboard",
      metric: "value",
      topN: 10,
      selectedProducts: [],
      prize1: "Employee of the Month + $500",
      prize2: "$250 Gift Card",
      prize3: "Free Team Lunch",
      managerMsg: "Our guests are everything. The scores are in — let's see who's delivering world-class experiences! ⭐",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: false,
      showHallOfFame: true,
      refreshCadence: 30,
    },
  },
  {
    id: "upsell-sprint",
    name: "Weekend Upsell Sprint",
    description: "Short, intense weekend competitions focused on add-on and upgrade sales. High energy, quick wins.",
    category: "Upsell",
    image: upsellSprintImg,
    icon: <Zap className="h-5 w-5" />,
    config: {
      name: "Weekend Upsell Sprint",
      metric: "value",
      topN: 8,
      selectedProducts: ["p5", "p6", "p7", "p8"],
      prize1: "$300 Cash Bonus",
      prize2: "$150 Cash Bonus",
      prize3: "$75 Cash Bonus",
      managerMsg: "48 hours. One leaderboard. Every upsell counts — jump passes, socks, parties. Let's smash last weekend's record! ⚡",
      showNames: true,
      showAvatars: true,
      showMovement: true,
      showCountdown: true,
      showHallOfFame: false,
      refreshCadence: 5,
    },
  },
];

const categories = ["All", "Membership", "Loyalty", "Product Sales", "Competition", "Feedback", "Upsell"];

const TemplatesPage = () => {
  const navigate = useNavigate();

  const handleSelectTemplate = (template: CampaignTemplate) => {
    const { icon, ...serializableTemplate } = template;
    navigate("/app/campaigns/new", { state: { template: serializableTemplate } });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Campaign Templates</h1>
        <p className="mt-1 text-muted-foreground">
          Pick a template to get started fast. Click to customise and launch.
        </p>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {categories.map((cat) => (
          <Badge
            key={cat}
            variant={cat === "All" ? "default" : "secondary"}
            className="cursor-pointer px-4 py-1.5 text-sm"
          >
            {cat}
          </Badge>
        ))}
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {campaignTemplates.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            onClick={() => handleSelectTemplate(t)}
            className="group cursor-pointer overflow-hidden rounded-xl border bg-card transition-all hover:border-primary hover:shadow-lg hover:shadow-primary/10"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={t.image}
                alt={t.name}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              {t.popular && (
                <Badge className="absolute right-3 top-3 bg-neon-orange text-white border-0">
                  Popular
                </Badge>
              )}
              <div className="absolute bottom-3 left-3">
                <Badge variant="secondary" className="bg-black/50 text-white border-0 backdrop-blur-sm">
                  {t.category}
                </Badge>
              </div>
            </div>

            <div className="p-5">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  {t.icon}
                </div>
                <h3 className="font-semibold">{t.name}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.description}</p>

              <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                <span className="text-sm font-semibold text-accent">{templateStats[t.id]?.avgRoi}</span>
                <span className="text-xs text-muted-foreground">avg ROI</span>
                <span className="text-muted-foreground/30">·</span>
                <span className="text-xs text-muted-foreground">{templateStats[t.id]?.venues} venues</span>
              </div>

              <div className="mt-3 flex items-center justify-center rounded-lg bg-primary/5 py-2 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                Use this template →
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TemplatesPage;
